// Данные демо-макета онбординга.
//
// Один набор на все четыре шага тура. Маршруты — IATA, они не переводятся ни
// на один язык; подписи ночёвок и названия вариантов лежат ключами словаря.
//
// Своих подписей здесь ровно столько, сколько нужно самому рассказу. Всё, что
// уже названо в интерфейсе — заголовок библиотеки, «Открыть», типы блоков,
// шаблоны поездок и частые блоки — берётся оттуда же, откуда его берут рабочие
// экраны: макет обязан стареть вместе с ними, а не отдельно.
//
// Суммы нигде не записаны руками: они считаются из строк. Иначе итог на
// последнем шаге рано или поздно разъедется с карточками на третьем — а весь
// смысл тура именно в том, что итог сходится.
import { VARIANT_COLORS } from '../store';
import { BLOCK_PRESETS, TRIP_TEMPLATES, type BlockSpec, type TripTemplate } from '../templates';

export interface MockTrip {
  id: 'asia' | 'europe' | 'america';
  nameKey: string;
  route: readonly [string, string];
}

/** Три направления первого шага. Европу тур открывает на втором. */
export const MOCK_TRIPS: readonly MockTrip[] = [
  { id: 'asia', nameKey: 'onboarding.mock.asia', route: ['LCA', 'NRT'] },
  { id: 'europe', nameKey: 'onboarding.mock.europe', route: ['LCA', 'BCN'] },
  { id: 'america', nameKey: 'onboarding.mock.america', route: ['LCA', 'JFK'] },
];

/** Какую поездку тур открывает на втором шаге. */
export const PICKED_TRIP: MockTrip['id'] = 'europe';

/**
 * Шаблоны под списком поездок и частые блоки в макете библиотеки — настоящие,
 * из `src/templates`. Придуманные рядом с ними разошлись бы с продуктом на
 * первом же изменении каталога, а человек увидел бы в туре то, чего в
 * приложении нет.
 */
export const MOCK_TEMPLATES: readonly TripTemplate[] = TRIP_TEMPLATES.slice(0, 3);
export const MOCK_PRESETS: readonly BlockSpec[] = BLOCK_PRESETS.slice(0, 4);

export interface MockRow {
  kind: 'flight' | 'lodging';
  /** Маршрут перелёта; у ночёвки его нет. */
  route?: readonly [string, string];
  /** Ключ подписи ночёвки: город в каждом языке склоняется по-своему, поэтому
   *  строка целиком, а не «Ночь в {city}». */
  labelKey?: string;
  /** Цена в евро, целыми: макет не про копейки. */
  price: number;
}

export interface MockVariant {
  id: 'cheap' | 'direct';
  nameKey: string;
  noteKey: string;
  color: string;
  rows: readonly MockRow[];
}

/**
 * Вся история тура в двух колонках: дешёвые билеты с пересадками тянут за
 * собой две ночёвки, прямые дороже, но ночевать негде и незачем.
 */
export const MOCK_VARIANTS: readonly MockVariant[] = [
  {
    id: 'cheap',
    nameKey: 'onboarding.mock.cheap',
    noteKey: 'onboarding.mock.cheap.note',
    color: VARIANT_COLORS[0],
    rows: [
      { kind: 'flight', route: ['LCA', 'ATH'], price: 39 },
      { kind: 'lodging', labelKey: 'onboarding.mock.night.ath', price: 130 },
      { kind: 'flight', route: ['ATH', 'MXP'], price: 48 },
      { kind: 'lodging', labelKey: 'onboarding.mock.night.mxp', price: 150 },
      { kind: 'flight', route: ['MXP', 'BCN'], price: 35 },
    ],
  },
  {
    id: 'direct',
    nameKey: 'onboarding.mock.direct',
    noteKey: 'onboarding.mock.direct.note',
    color: VARIANT_COLORS[1],
    rows: [
      { kind: 'flight', route: ['LCA', 'ATH'], price: 165 },
      { kind: 'flight', route: ['ATH', 'BCN'], price: 190 },
    ],
  },
];

/** Сумма по одному типу строк варианта. */
export function sumOf(variant: MockVariant, kind: MockRow['kind']): number {
  return variant.rows.reduce((acc, row) => (row.kind === kind ? acc + row.price : acc), 0);
}

export function totalOf(variant: MockVariant): number {
  return variant.rows.reduce((acc, row) => acc + row.price, 0);
}

function byId(id: MockVariant['id']): MockVariant {
  const found = MOCK_VARIANTS.find((v) => v.id === id);
  if (!found) throw new Error(`вариант «${id}» отсутствует в демо-сценарии`);
  return found;
}

/** Победитель сравнения — тот, у кого итог меньше. Считается, а не назначается. */
export const WINNER: MockVariant['id'] =
  totalOf(byId('cheap')) <= totalOf(byId('direct')) ? 'cheap' : 'direct';

/** Насколько победитель дешевле проигравшего — цифра для бейджа на 4-м шаге. */
export const SAVINGS: number = Math.abs(totalOf(byId('cheap')) - totalOf(byId('direct')));

/**
 * Цены макета — иллюстрация, а не состояние: справочника валют у него нет,
 * поэтому formatMoney тут неприменим и знак ставится напрямую.
 */
export function eur(amount: number): string {
  return `€${amount}`;
}

// ── Шаги тура ─────────────────────────────────────────────────────────────

/**
 * Порядок шагов: сначала заводим поездку, потом открываем её, потом узнаём,
 * откуда в ней берутся блоки, собираем из них два варианта, сравниваем итог —
 * и только в конце зовём соавтора и показываем результат наружу. Приглашение
 * и ссылка стоят последними намеренно: делиться нечем, пока нечего показать.
 */
export const STEPS = [
  'trips',
  'open',
  'source',
  'build',
  'totals',
  'access',
  'share',
] as const;

export type StepId = (typeof STEPS)[number];

/** Какое окно лежит поверх редактора на этом шаге. */
export type StageWindow = 'source' | 'access' | 'share';

export interface StageView {
  /** Список поездок: виден, уходит, скрыт. */
  trips: 'shown' | 'leaving' | 'hidden';
  /** Редактор: скрыт, въезжает на смену списку, стоит на месте. */
  editor: 'hidden' | 'entering' | 'shown';
  /** Блоки и итоги в колонках: пусто, наполняются, стоят. */
  fill: 'empty' | 'entering' | 'filled';
  /** Лента варианта доехала до итогов. */
  scrolled: boolean;
  window: StageWindow | null;
}

/**
 * Раскладка шага на состояния слоёв. Живёт рядом со сценарием, а не в стилях:
 * селекторам достаётся смысл (`[data-fill='entering']`), а не номер шага,
 * и вставленный посередине шаг не заставляет пересчитывать весь CSS.
 */
export function stageView(step: number): StageView {
  const id = STEPS[Math.min(STEPS.length, Math.max(1, step)) - 1];
  const filledFrom = STEPS.indexOf('build');
  const index = STEPS.indexOf(id);

  return {
    trips: id === 'trips' ? 'shown' : id === 'open' ? 'leaving' : 'hidden',
    editor: id === 'trips' ? 'hidden' : id === 'open' ? 'entering' : 'shown',
    fill: index < filledFrom ? 'empty' : id === 'build' ? 'entering' : 'filled',
    scrolled: index >= STEPS.indexOf('totals'),
    window: id === 'source' || id === 'access' || id === 'share' ? id : null,
  };
}
